/**
 * Transcript parsing and formatting.
 *
 * Pure, isomorphic helpers (no React, no Node/Web-only APIs) for turning caption
 * files (SRT or WebVTT — the two formats Wistia's Data API returns) into a
 * structured cue list, and for rendering timestamps as both human strings and
 * ISO-8601 durations for schema.org markup.
 */

export type TranscriptCue = {
  /** Cue start, in seconds. */
  start: number;
  /** Cue end, in seconds. */
  end: number;
  /** Cue text, with inline caption markup stripped and whitespace collapsed. */
  text: string;
};

const TIMING_SEPARATOR = "-->";

/**
 * Parse a single timecode (`HH:MM:SS,mmm`, `HH:MM:SS.mmm`, or `MM:SS.mmm`) to
 * seconds. Returns `NaN` for anything unparseable so callers can skip bad cues.
 */
function timecodeToSeconds(raw: string): number {
  const parts = raw.trim().replace(",", ".").split(":");
  if (parts.length < 2 || parts.length > 3) return NaN;
  const nums = parts.map(Number);
  if (nums.some((n) => Number.isNaN(n))) return NaN;
  const [a, b, c] = nums;
  // 3 parts → h:m:s, 2 parts → m:s
  return parts.length === 3 ? a * 3600 + b * 60 + c : a * 60 + b;
}

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

/** Decode HTML entities (named + numeric) so encoded artifacts don't leak into crawlable text. */
function decodeEntities(text: string): string {
  return text.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (match, code: string) => {
    if (code[0] === "#") {
      const isHex = code[1] === "x" || code[1] === "X";
      const point = parseInt(code.slice(isHex ? 2 : 1), isHex ? 16 : 10);
      return Number.isFinite(point) && point >= 0 && point <= 0x10ffff
        ? String.fromCodePoint(point)
        : match;
    }
    return NAMED_ENTITIES[code.toLowerCase()] ?? match;
  });
}

/**
 * Strip WebVTT inline markup (`<c>`, `<v Name>`, `<00:00:01.000>` cue timestamps),
 * decode HTML entities, and collapse whitespace. Tags are removed before decoding
 * so an escaped `&lt;b&gt;` survives as literal text rather than being re-stripped.
 */
function stripCaptionMarkup(text: string): string {
  return decodeEntities(text.replace(/<[^>]+>/g, ""))
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Parse SRT or WebVTT caption text into cues. Blocks are separated by blank
 * lines; a block is kept only if it has a valid timing line. Optional index
 * lines (SRT), cue identifiers (VTT), and trailing cue settings (VTT, e.g.
 * `align:start`) are ignored. Malformed blocks are skipped, never thrown.
 */
export function parseCaptions(input: string): TranscriptCue[] {
  if (!input?.trim()) return [];

  const body = input.replace(/^﻿/, "").replace(/\r\n/g, "\n");
  const blocks = body.split(/\n\s*\n/);
  const cues: TranscriptCue[] = [];

  for (const block of blocks) {
    const lines = block.split("\n");
    const timingIndex = lines.findIndex((l) => l.includes(TIMING_SEPARATOR));
    if (timingIndex === -1) continue;

    const [startRaw, endRaw] = lines[timingIndex].split(TIMING_SEPARATOR);
    if (endRaw === undefined) continue;

    const start = timecodeToSeconds(startRaw);
    // Drop any trailing cue settings after the end timecode (VTT).
    const end = timecodeToSeconds(endRaw.trim().split(/\s+/)[0]);
    if (Number.isNaN(start) || Number.isNaN(end)) continue;

    const text = stripCaptionMarkup(lines.slice(timingIndex + 1).join(" "));
    if (text) cues.push({ start, end, text });
  }

  return cues;
}

/** Join cues into a single normalized plaintext string (the AEO payload). */
export function cuesToPlainText(cues: TranscriptCue[]): string {
  return cues
    .map((c) => c.text)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Turn untimecoded caption plaintext into evenly-distributed cues, so the AEO
 * transcript still renders when a source has no timings. Timestamps are derived,
 * not authoritative — the transcript is hidden, so they only label the text.
 */
export function plaintextToCues(
  text: string,
  durationSeconds: number,
): TranscriptCue[] {
  const clean = stripCaptionMarkup(text);
  if (!clean) return [];

  const total = Number.isFinite(durationSeconds)
    ? Math.max(0, durationSeconds)
    : 0;
  const segments =
    clean
      .match(/[^.!?]+[.!?]*/g)
      ?.map((s) => s.trim())
      .filter(Boolean) ?? [];
  const parts = segments.length ? segments : [clean];
  const span = total > 0 ? total / parts.length : 0;

  return parts.map((segment, index) => ({
    start: Math.round(index * span * 10) / 10,
    end:
      index === parts.length - 1
        ? Math.round(total * 10) / 10
        : Math.round((index + 1) * span * 10) / 10,
    text: segment,
  }));
}

/** Format seconds as a human timestamp: `m:ss`, or `h:mm:ss` past an hour. */
export function formatTimestamp(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, "0");
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${ss}`;
  return `${m}:${ss}`;
}

/** Format seconds as an ISO-8601 duration (`PT1M12S`) for schema.org. */
export function secondsToIso8601Duration(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  let out = "PT";
  if (h) out += `${h}H`;
  if (m) out += `${m}M`;
  if (s || (!h && !m)) out += `${s}S`;
  return out;
}
